import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FORECASTLY_LOGO_LIGHT, PROFITMIND_LOGO } from "@/lib/brand";
import {
  SignOut,
  Gauge,
  Table,
  ChartLineUp,
  Target,
  Gear,
  CaretDown,
  UserCircle,
  Clock,
  Lock,
  WarningCircle,
  ShieldStar,
  Lifebuoy,
  RocketLaunch,
  VideoCamera,
  Gift,
} from "@phosphor-icons/react";

const LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: Gauge, testid: "nav-dashboard" },
  { to: "/forecast", label: "Forecast", icon: Table, testid: "nav-forecast" },
  { to: "/actuals", label: "Actuals", icon: ChartLineUp, testid: "nav-actuals" },
  { to: "/goals", label: "Goals", icon: Target, testid: "nav-goals" },
];

// Iter38 — calendar-day trial countdown. Rolls at LOCAL MIDNIGHT so
// signing up at 6pm Aug 30 shows "10 days left" today, "9 days left"
// at midnight tomorrow, not another 24 hours later. Both dates are
// snapped to midnight before diffing so the answer is always a whole
// number of calendar days.
function calendarDaysLeft(trialEndsIso) {
  if (!trialEndsIso) return 0;
  const end = new Date(trialEndsIso);
  end.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((end.getTime() - today.getTime()) / 86400000));
}

/**
 * Iter21 trial banner — surfaces trial status across the app.
 *   trial:      "X days left · Manage subscription"
 *   past_due:   payment failed after trial
 *   read_only:  30-day grace after cancel
 *   expired:    hard-locked
 */
function TrialBanner({ user }) {
  const plan = user?.plan;
  if (!plan) return null;
  if (plan === "trial") {
    const ends = user?.trial_ends_at;
    if (!ends) return null;
    const daysLeft = calendarDaysLeft(ends);
    return (
      <div
        className="bg-[#FFBD4A] border-b-2 border-black"
        data-testid="trial-banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <Clock size={16} weight="bold" />
            <span data-testid="trial-days-left">
              <strong>{daysLeft} {daysLeft === 1 ? "day" : "days"}</strong> left of your free
              trial. You'll be billed on day 11 unless you cancel.
            </span>
          </div>
          <Link
            to="/account#your-plan"
            className="text-sm font-semibold underline"
            data-testid="trial-manage-link"
          >
            Manage subscription →
          </Link>
        </div>
      </div>
    );
  }
  if (plan === "past_due") {
    return (
      <div className="bg-red-50 border-b-2 border-red-600" data-testid="past-due-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-red-800">
            <WarningCircle size={16} weight="bold" />
            <span>
              <strong>Your last payment failed.</strong> Update your card in the billing portal to
              keep your forecast live.
            </span>
          </div>
          <Link
            to="/account"
            className="text-sm font-semibold underline text-red-800"
            data-testid="past-due-manage-link"
          >
            Update card →
          </Link>
        </div>
      </div>
    );
  }
  if (plan === "read_only") {
    return (
      <div className="bg-black text-white border-b-2 border-black" data-testid="read-only-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <Lock size={16} weight="bold" />
            <span>
              Your subscription has ended. You have <strong>view-only access</strong> for the next
              30 days.
            </span>
          </div>
          <Link
            to="/account"
            className="text-sm font-semibold underline"
            data-testid="read-only-reactivate-link"
          >
            Reactivate →
          </Link>
        </div>
      </div>
    );
  }
  if (plan === "soft_deleted") {
    return (
      <div className="bg-red-800 text-white border-b-2 border-black" data-testid="soft-deleted-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <Lock size={16} weight="bold" />
            <span>
              Your account has been archived. Contact <strong>support@forecastly.me</strong> within
              60 days to restore your data — after that, it's permanently deleted.
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

export default function AppShell() {
  const { user, logout, setAuthFromToken } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const exitImpersonation = async () => {
    // Restore the admin's original token (saved when we started impersonating).
    const adminToken = localStorage.getItem("cc_admin_token");
    if (!adminToken) {
      // Fallback — just sign out. Should not happen in practice.
      logout();
      nav("/", { replace: true });
      return;
    }
    localStorage.removeItem("cc_admin_token");
    // Fetch the admin's /auth/me so the shell reloads with correct user info.
    try {
      const { default: axios } = await import("axios");
      axios.defaults.headers.common["Authorization"] = `Bearer ${adminToken}`;
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`);
      setAuthFromToken(adminToken, res.data);
      nav("/admin", { replace: true });
    } catch {
      logout();
      nav("/", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {user?.impersonated && (
        <div
          className="bg-red-600 text-white border-b-2 border-black"
          data-testid="impersonation-banner"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <ShieldStar size={16} weight="bold" />
              <span>
                <strong>Impersonating</strong> {user?.email} — everything you do here
                affects their live account.
              </span>
            </div>
            <button
              type="button"
              onClick={exitImpersonation}
              className="text-sm font-semibold underline hover:no-underline"
              data-testid="impersonation-exit"
            >
              Exit impersonation →
            </button>
          </div>
        </div>
      )}
      <TrialBanner user={user} />
      <header className="border-b border-black/15 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-28 gap-4">
          <Link to="/dashboard" className="flex items-center" data-testid="app-logo">
            <img
              src={FORECASTLY_LOGO_LIGHT}
              alt="forecastly"
              className="w-[220px] h-auto object-contain"
            />
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-xl font-heading uppercase text-sm tracking-wide transition-all border-[1.5px] ${
                    isActive
                      ? "bg-[#FFBD4A] text-black border-black shadow-[2px_2px_0_#000]"
                      : "border-transparent text-black hover:border-black hover:bg-[#FFF7E5]"
                  }`
                }
              >
                <l.icon size={18} weight="bold" />
                <span className="hidden md:inline">{l.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="user-menu-btn"
              className="flex items-center gap-2 px-3 py-2 rounded-lg border-[1.5px] border-black/40 hover:bg-[#FFF7E5] transition-colors"
            >
              <UserCircle size={20} weight="duotone" />
              <div className="hidden sm:block text-left leading-tight">
                <div className="text-sm font-semibold flex items-center gap-1.5">
                  {user?.name}
                  {user?.is_admin && (
                    <span
                      className="text-[9px] font-heading uppercase tracking-widest bg-black text-[#FFBD4A] px-1.5 py-0.5 rounded"
                      data-testid="admin-chip"
                    >
                      Admin
                    </span>
                  )}
                </div>
                <div className="text-xs text-black/55">{user?.email}</div>
              </div>
              <CaretDown size={14} weight="bold" />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-56 bg-white border-[1.5px] border-black rounded-xl shadow-lg overflow-hidden z-40"
                data-testid="user-menu"
              >
                <div className="px-4 py-3 border-b border-black/10">
                  <div className="text-xs uppercase tracking-wider text-black/55">Signed in</div>
                  <div className="text-sm font-semibold truncate">{user?.email}</div>
                </div>
                <Link
                  to="/account"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 hover:bg-[#FFF7E5] text-sm font-semibold"
                  data-testid="menu-account"
                >
                  <Gear size={16} weight="bold" /> Account settings
                </Link>
                {user?.is_admin && (
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 hover:bg-[#FFF7E5] text-sm font-semibold border-t border-black/10"
                    data-testid="menu-admin"
                  >
                    <ShieldStar size={16} weight="bold" /> Admin dashboard
                  </Link>
                )}
                <Link
                  to="/refer"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 hover:bg-[#FFF7E5] text-sm font-semibold border-t border-black/10"
                  data-testid="menu-refer"
                >
                  <Gift size={16} weight="bold" /> Refer a friend
                </Link>
                <Link
                  to="/help"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 hover:bg-[#FFF7E5] text-sm font-semibold border-t border-black/10"
                  data-testid="menu-help"
                >
                  <Lifebuoy size={16} weight="bold" /> Help & support
                </Link>
                <Link
                  to="/roadmap"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 hover:bg-[#FFF7E5] text-sm font-semibold border-t border-black/10"
                  data-testid="menu-roadmap"
                >
                  <RocketLaunch size={16} weight="bold" /> Product roadmap
                </Link>
                <Link
                  to="/community"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 hover:bg-[#FFF7E5] text-sm font-semibold border-t border-black/10"
                  data-testid="menu-community"
                >
                  <VideoCamera size={16} weight="bold" /> Community & events
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    nav("/", { replace: true });
                    window.location.href = "/";
                  }}
                  data-testid="menu-logout"
                  className="w-full flex items-center gap-2 px-4 py-3 hover:bg-[#FFF7E5] text-sm font-semibold border-t border-black/10"
                >
                  <SignOut size={16} weight="bold" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Outlet />
      </main>
      <footer className="border-t border-black/15 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex items-start gap-4 md:flex-1">
            <a
              href="https://profitmind.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit ProfitMIND"
              className="inline-block shrink-0"
              data-testid="app-footer-logo"
            >
              <img src={PROFITMIND_LOGO} alt="ProfitMIND" className="h-14 w-auto rounded-[10px]" />
            </a>
            <div className="text-xs text-black/70 leading-relaxed">
              © {new Date().getFullYear()} ProfitMIND LTD. All rights reserved.
              <br />
              forecastly is a trading name of ProfitMIND LTD.
              <br />
              <span className="whitespace-nowrap">Registered in England &amp; Wales. Reg. no. 09918255.</span>
            </div>
          </div>
          <nav className="flex flex-col md:items-end gap-2 text-xs md:flex-1">
            <div className="flex flex-wrap md:justify-end gap-x-4 gap-y-2 md:flex-nowrap md:whitespace-nowrap">
              <a
                href="https://profitmind.co.uk/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline font-semibold"
                data-testid="app-footer-privacy"
              >
                Privacy Policy
              </a>
              <a
                href="https://profitmind.co.uk/cookie-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline font-semibold"
                data-testid="app-footer-cookies"
              >
                Cookie Policy
              </a>
              <a
                href="https://profitmind.co.uk/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline font-semibold"
                data-testid="app-footer-terms"
              >
                Terms of Use
              </a>
              <a
                href="https://profitmind.co.uk/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline font-semibold"
                data-testid="app-footer-terms-conditions"
              >
                Terms and Conditions
              </a>
            </div>
            <span className="text-black/55 uppercase tracking-widest md:text-right">
              Built by ProfitMIND · Follow The Cash<sup>TM</sup> · Own Your Future
            </span>
          </nav>
        </div>
      </footer>
    </div>
  );
}
